import type { User } from 'firebase/auth';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { notificationService } from '@/services';
import type { UserProfile } from '@/types';
import { useConversations } from './useConversations';
import { useMessages } from './useMessages';
import { useUser } from './useUser';

/** Active DM from `/chat/:id` — conversation, partner, live messages, block guard. */
export function useConversation(
  conversationId: string | undefined,
  user: User | null,
  currentUser: UserProfile | null
) {
  const {
    conversations,
    markConversationRead,
    markConversationUnread,
    blockedUids,
    blockedByUids,
    isLoading: conversationsLoading,
    isError: conversationsError,
    refetch: refetchConversations,
  } = useConversations(user);

  const conversation = useMemo(
    () => (conversationId ? (conversations.find(c => c.id === conversationId) ?? null) : null),
    [conversations, conversationId]
  );

  const partnerId = useMemo(() => {
    if (!conversation || !user) return undefined;
    return conversation.userUids.find(id => id !== user.uid);
  }, [conversation, user]);

  const isBlocked = Boolean(
    partnerId && (blockedUids.includes(partnerId) || blockedByUids.includes(partnerId))
  );

  const {
    user: partner,
    isLoading: partnerLoading,
    isError: partnerError,
    refetch: refetchPartner,
  } = useUser(isBlocked ? undefined : partnerId);

  const {
    messages,
    sendMessage,
    isLoading: messagesLoading,
    isError: messagesError,
    refetch: refetchMessages,
  } = useMessages(!isBlocked && conversationId ? conversationId : null, user?.uid ?? null);

  // Set when the user flags the thread unread from inside the chat — stops the
  // auto mark-read effect below from instantly clearing our own flag again.
  const suppressAutoReadRef = useRef(false);

  useEffect(() => {
    if (!conversationId || !conversation || isBlocked) return;
    if (suppressAutoReadRef.current) return;
    void markConversationRead(conversationId).catch(err => {
      console.error('Failed to mark conversation read:', err);
    });
    void notificationService.dismissNotificationsForConversation(conversationId).catch(err => {
      console.error('Failed to dismiss chat notifications:', err);
    });
  }, [conversationId, conversation, isBlocked, markConversationRead]);

  /** Flag unread from inside the chat — pairs with navigating back to the inbox. */
  const flagConversationUnread = useCallback(
    async (conversationId: string) => {
      suppressAutoReadRef.current = true;
      await markConversationUnread(conversationId);
    },
    [markConversationUnread]
  );

  const sharedIntentions = useMemo(() => {
    const mine = new Set(currentUser?.intentions ?? []);
    return (partner?.intentions ?? []).filter(id => mine.has(id));
  }, [currentUser?.intentions, partner?.intentions]);

  const sharedSpots = useMemo(() => {
    const mine = new Set(currentUser?.spots ?? []);
    return (partner?.spots ?? []).filter(spot => mine.has(spot));
  }, [currentUser?.spots, partner?.spots]);

  const refetch = useCallback(() => {
    void refetchConversations();
    void refetchPartner();
    void refetchMessages();
  }, [refetchConversations, refetchPartner, refetchMessages]);

  return {
    conversation,
    partner,
    messages,
    sendMessage,
    isBlocked,
    flagConversationUnread,
    sharedIntentions,
    sharedSpots,
    isLoading: conversationsLoading || partnerLoading || messagesLoading,
    isError: conversationsError || partnerError || messagesError,
    refetch,
  };
}
